import { useState } from 'react'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'
import Modal from './Modal'
import Timestamp from './Timestamp'
import useBilling from '../models/useBilling'
import {
  deadlinePresets,
  planDefaultHours,
  planMaxHours,
  removesDeadline,
  type DeadlinePreset,
  type TaskLifecycle,
} from '../utilities/taskDeadline'

/**
 * The control that moves a machine's deadline (router#1216).
 *
 * ⚠⚠ EVERY CHOICE IS AN INSTANT FROM NOW AND THE MENU SAYS SO, because that is
 * what the router's `max_hours` means. A menu labelled "+4 hours" over that API
 * would CUT the life of any machine with more than four hours left, and the call
 * would report success — so each row reads "In 4 hours", prints the instant it
 * produces, and is marked when it lands EARLIER than the deadline the machine
 * holds now. Shortening is a legitimate act; doing it by accident is not.
 *
 * ⚠ Labelled "Give more time" rather than "Extend": extend names only one
 * direction, and this menu can also bring a stop forward.
 *
 * ⚠⚠ "No deadline" IS CONFIRMED SEPARATELY AND EVERY OTHER CHOICE IS NOT. The
 * others MOVE a bound and are undone by picking another; this one REMOVES it, and
 * what it removes is the only thing standing between a forgotten container and an
 * unbounded bill — 14 of them were 96.9% of all compute we have ever served. It
 * is also the one value that could be misread as "stop now", which it is not.
 */
export default function ExtendDeadlineButton({
  lifecycle,
  now,
  allowUnlimited,
  disabled,
  busy,
  onPick,
}: Readonly<{
  lifecycle: TaskLifecycle
  now: Date
  allowUnlimited: boolean
  disabled: boolean
  busy: boolean
  // ⚠ `number` and not `number | null` since this PR. The only preset that carried `null` was
  // "My plan's default", which is gone; every entry now names its own `max_hours`.
  onPick: (hours: number) => void
}>) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  // ⚠ Holds the PRESET and not a boolean, so the confirmation can name what is
  // about to happen rather than restating a rule the reader has to match up.
  const [confirming, setConfirming] = useState<DeadlinePreset | null>(null)
  const plans = useBilling(s => s.plans)
  const plan = useBilling(s => s.plan)
  const presets = deadlinePresets(lifecycle, now, allowUnlimited, plans, planDefaultHours(plan), planMaxHours(plan))

  const pick = (preset: DeadlinePreset) => {
    setAnchor(null)
    if (removesDeadline(preset)) setConfirming(preset)
    else onPick(preset.hours)
  }

  return (
    <>
      <Button size="small" disabled={disabled} onClick={e => setAnchor(e.currentTarget)}>
        {busy ? 'Saving…' : 'Give more time'}
      </Button>
      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {presets.map(preset => (
          <MenuItem key={preset.label} disabled={preset.disabled} onClick={() => pick(preset)}>
            <ListItemText
              primary={preset.label}
              secondary={
                /* ⚠ The instant is the whole point of the row: it is what turns
                   an ambiguous "4 hours" into an outcome the reader can check
                   against the Deadline column beside it. Only No deadline has
                   none, because there IS no instant. A greyed entry says where
                   it is available instead. */
                preset.disabled ? (
                  (preset.hint ?? 'not on your plan')
                ) : preset.at ? (
                  <>
                    <Timestamp date={preset.at} variant="full" />
                    {/* ⚠ NOT "earlier than now" — every preset is `now + N hours`, so none of
                        them is. What `shortens` compares is this instant against the deadline
                        the machine HOLDS, and saying "now" printed a sentence that contradicted
                        the timestamp on its own line. */}
                    {preset.shortens && ' — sooner than this machine’s deadline now, so this brings the stop forward'}
                  </>
                ) : (
                  'it runs, and bills, until you terminate it'
                )
              }
              slotProps={{
                secondary: {
                  sx: {
                    color:
                      !preset.disabled && (preset.shortens || removesDeadline(preset))
                        ? 'error.main'
                        : 'text.secondary',
                  },
                },
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* ⚠⚠ A SEPARATE CONFIRMATION, and only for the choice that REMOVES the
          bound. It is not DialogConfirm, which wraps a clickable child — there is
          no child here, the trigger is a menu item that has already closed. */}
      <Modal open={confirming !== null} onClose={() => setConfirming(null)} color="error.main">
        <DialogTitle variant="subtitle1" color="error">
          Run this machine with no time limit?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" component="div">
            Nothing will stop this machine. It runs, and bills, until you terminate it. Give it a
            deadline again at any time from this same menu.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" sx={{ color: 'GrayText' }} onClick={() => setConfirming(null)}>
            Cancel
          </Button>
          <Button
            autoFocus
            variant="contained"
            color="error"
            // ⚠⚠ THE SAME GUARDS THE MENU ENTRY CARRIES, BECAUSE THIS IS A SECOND WAY INTO
            // `setDeadline` AND IT OUTLIVES THE MENU. The dialog stays open across another row's
            // pick: open it here, start an extend on a different row, confirm here, and two calls
            // run at once over ONE `deadlineChanging` slot — the first to finish clears it while
            // the second is still out, which is exactly the overlap `deprovisioning` documents as
            // the reason for one slot and a locked table.
            //
            // ⚠⚠ `allowUnlimited` IS THE OTHER HALF, and it is why the menu's greying alone is
            // not the guarantee. `useBilling.get()` re-runs on any `organizations` change and on
            // an org switch, so the plan can lose `compute_deadline_optional` while this dialog
            // is open: the menu entry greys out, this button does not, and pressing it sends
            // `max_hours: 0` to a router that refuses it.
            disabled={disabled || !allowUnlimited}
            onClick={() => {
              // ⚠ `preset.hours` and never a literal 0 — one place decides what
              // "no deadline" is on the wire, and it is the preset list.
              const preset = confirming
              setConfirming(null)
              if (preset) onPick(preset.hours)
            }}
          >
            Remove the time limit
          </Button>
        </DialogActions>
      </Modal>
    </>
  )
}
